import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { calcDoc, fmt, genNum, today } from '../../utils/calc';
import { buildClientEmail, buildAdminEmail } from '../../utils/emailBuilder';
import PrintPreview from './PrintPreview';
import './Modal.css';

const emptyLine = () => ({ desc: '', qty: 1, unit: 'Unité', prix: 0 });

export default function DevisModal({ onClose }) {
  const { currentUser, data, saveTransaction, showToast } = useApp();
  const [num,    setNum]    = useState(genNum('DEV'));
  const [date,   setDate]   = useState(today());
  const [valid,  setValid]  = useState(30);
  const [cnom,   setCnom]   = useState('');
  const [ctel,   setCtel]   = useState('');
  const [cemail, setCemail] = useState('');
  const [cadr,   setCadr]   = useState('');
  const [lines,  setLines]  = useState([emptyLine()]);
  const [tva,    setTva]    = useState(0);
  const [remise, setRemise] = useState(0);
  const [notes,  setNotes]  = useState('Ce devis est valable 30 jours à compter de sa date d\'émission.\nTout commencement de travail vaut acceptation du présent devis.');
  const [preview,setPreview]= useState(false);
  const [mailInfo,setMailInfo]=useState(null);

  const totals = calcDoc(lines, tva, remise, 0);

  function updateLine(i, key, val) {
    setLines(l => l.map((ln, j) => j === i ? { ...ln, [key]: val } : ln));
  }
  function removeLine(i) { setLines(l => l.filter((_, j) => j !== i)); }

  function handleSave() {
    saveTransaction('devis', lines, { num, date, valid, cnom, ctel, cemail, cadr, notes }, totals);
    showToast('Devis enregistré !');
  }

  function handleMail() {
    const worker  = currentUser?.name || '—';
    const dateStr = new Date(date).toLocaleDateString('fr-FR');
    const ctx = { type:'devis', num, dateStr, valid, cnom, cemail, worker, totals, contact: data.contact, paiement: data.paiement };
    const bodyClient = encodeURIComponent(buildClientEmail(ctx));
    const bodyAdmin  = encodeURIComponent(buildAdminEmail(ctx));
    setMailInfo({
      clientHref: cemail
        ? `mailto:${cemail}?subject=${encodeURIComponent('KOTOSTECHNOLOGY – Devis N° ' + num)}&body=${bodyClient}`
        : null,
      adminHref: `mailto:${data.contact.email}?subject=${encodeURIComponent('[KOTOS] Devis N° ' + num + ' – ' + (cnom||'Client') + ' – par ' + worker)}&body=${bodyAdmin}`,
      cemail,
    });
    handleSave();
  }

  return (
    <div className="overlay open">
      <div className="modal modal-lg">
        <div className="mhead">
          <h2>📋 Devis — <span className="worker-lbl">{currentUser?.name}</span></h2>
          <button className="mclose" onClick={onClose}>✕</button>
        </div>
        <div className="mbody">
          <div className="grid3">
            <div className="fg2"><label>N° Devis</label><input value={num} onChange={e=>setNum(e.target.value)}/></div>
            <div className="fg2"><label>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
            <div className="fg2"><label>Validité (jours)</label><input type="number" value={valid} min={1} onChange={e=>setValid(+e.target.value)}/></div>
          </div>
          <div className="grid3">
            <div className="fg2"><label>Client – Nom *</label><input value={cnom} onChange={e=>setCnom(e.target.value)} placeholder="Nom du client"/></div>
            <div className="fg2"><label>Téléphone</label><input value={ctel} onChange={e=>setCtel(e.target.value)} placeholder="+229 XX XX"/></div>
            <div className="fg2"><label>Email client</label><input type="email" value={cemail} onChange={e=>setCemail(e.target.value)} placeholder="client@email.com"/></div>
          </div>
          <div className="fg2"><label>Adresse client</label><input value={cadr} onChange={e=>setCadr(e.target.value)} placeholder="Adresse"/></div>

          <h3 className="section-sep">Prestations prévues</h3>
          <div className="lines-header">
            <span>Description</span><span>Qté</span><span>Unité</span><span>Prix estimé</span><span/>
          </div>
          {lines.map((ln, i) => (
            <div key={i} className="fact-line">
              <input className="fact-inp" value={ln.desc} onChange={e=>updateLine(i,'desc',e.target.value)} placeholder="Description"/>
              <input className="fact-inp center" type="number" min={1} value={ln.qty} onChange={e=>updateLine(i,'qty',+e.target.value)}/>
              <input className="fact-inp" value={ln.unit} onChange={e=>updateLine(i,'unit',e.target.value)}/>
              <input className="fact-inp" type="number" min={0} value={ln.prix} onChange={e=>updateLine(i,'prix',+e.target.value)}/>
              <button className="fact-del" onClick={()=>removeLine(i)}>✕</button>
            </div>
          ))}
          <button className="add-line-btn" onClick={()=>setLines(l=>[...l,emptyLine()])}>+ Ligne</button>

          <div className="grid2" style={{marginTop:14}}>
            <div className="fg2"><label>TVA %</label><input type="number" value={tva} min={0} max={100} onChange={e=>setTva(+e.target.value)}/></div>
            <div className="fg2"><label>Remise %</label><input type="number" value={remise} min={0} max={100} onChange={e=>setRemise(+e.target.value)}/></div>
          </div>

          <div className="totals-box">
            <div className="tot-row"><span>Sous-total :</span><span>{fmt(totals.sub)}</span></div>
            {totals.remisePct > 0 && <div className="tot-row"><span>Remise ({totals.remisePct}%) :</span><span>– {fmt(totals.remiseAmt)}</span></div>}
            {totals.tvaPct > 0 && <div className="tot-row"><span>TVA ({totals.tvaPct}%) :</span><span>{fmt(totals.tvaAmt)}</span></div>}
            <div className="tot-row big"><span>TOTAL ESTIMÉ :</span><span>{fmt(totals.total)}</span></div>
          </div>

          <div className="fg2" style={{marginTop:12}}>
            <label>Observations / Conditions</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}/>
          </div>

          <div className="action-btns">
            <button className="abtn abtn-blue"  onClick={()=>setPreview(p=>!p)}>{preview ? "▲ Masquer l'aperçu" : "👁 Aperçu / Imprimer"}</button>
            <button className="abtn abtn-green" onClick={handleMail}>📧 Préparer email</button>
            <button className="abtn abtn-muted" onClick={handleSave}>💾 Enregistrer</button>
          </div>

          {mailInfo && (
            <div className="mail-ready">
              <p className="mail-title">✓ Email prêt — aucune pièce jointe, expéditeur : <strong>{data.contact.email}</strong></p>
              <div className="mail-btns">
                {mailInfo.clientHref
                  ? <a href={mailInfo.clientHref} className="abtn abtn-green" style={{textDecoration:'none'}}>📧 Envoyer au client ({mailInfo.cemail})</a>
                  : <span className="mail-warn">⚠ Email client non fourni</span>}
                <a href={mailInfo.adminHref} className="abtn abtn-blue" style={{textDecoration:'none'}}>📨 Notifier l'admin</a>
              </div>
            </div>
          )}

          {preview && (
            <PrintPreview type="devis"
              meta={{ num, date, valid, cnom, ctel, cemail, cadr, notes, worker: currentUser?.name }}
              lines={lines} totals={totals}
            />
          )}
        </div>
      </div>
    </div>
  );
}
